(() => {
  if (!('customElements' in window) || customElements.get('maxler-circular-reviews')) return;

  const FOCUSABLE = 'a, button, input, select, textarea, [tabindex]';
  const DURATION = 280;
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  class MaxlerCircularReviews extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.setAttribute('data-premium-reviews', '');
      this._viewport = this.querySelector('[data-reviews-viewport]');
      this._track = this.querySelector('[data-reviews-track]');
      this._rail = this.querySelector('.maxler-white-review-rail');
      this._prev = this.querySelector('[data-circular-prev]');
      this._next = this.querySelector('[data-circular-next]');
      this._status = this.querySelector('[data-circular-status]');
      this._reducedMotion = window.matchMedia?.(REDUCED_MOTION);
      this._cards = [];
      this._clones = [];
      this._queue = [];
      this._animation = null;
      this._currentIndex = 0;
      this._busy = false;
      this._running = false;
      this._interactionVersion = (this._interactionVersion || 0) + 1;
      this._pointer = null;
      this._suppressClick = false;
      this._lastViewportWidth = -1;
      this._lastRailWidth = -1;

      this._onPrevious = () => this.enqueue(-1);
      this._onNext = () => this.enqueue(1);
      this._onPointerDown = (event) => this.pointerDown(event);
      this._onPointerMove = (event) => this.pointerMove(event);
      this._onPointerUp = (event) => this.pointerUp(event);
      this._onPointerCancel = () => { this._pointer = null; };
      this._onClickCapture = (event) => {
        const suppress = this._suppressClick && event.detail > 0;
        this._suppressClick = false;
        if (!suppress) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      this._onKeyDown = (event) => this.keyDown(event);
      this._onFocusIn = (event) => this.revealFocusedCard(event.target);
      this._onBlockSelect = (event) => this.handleBlockSelect(event);

      this._prev?.addEventListener('click', this._onPrevious);
      this._next?.addEventListener('click', this._onNext);
      this._viewport?.addEventListener('pointerdown', this._onPointerDown, { passive: true });
      this._viewport?.addEventListener('pointermove', this._onPointerMove, { passive: true });
      this._viewport?.addEventListener('pointerup', this._onPointerUp, { passive: true });
      this._viewport?.addEventListener('pointercancel', this._onPointerCancel, { passive: true });
      this._viewport?.addEventListener('click', this._onClickCapture, true);
      this._viewport?.addEventListener('keydown', this._onKeyDown);
      this.addEventListener('focusin', this._onFocusIn);
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this.checkWidths());
        if (this._viewport) this._resizeObserver.observe(this._viewport);
        if (this._rail) this._resizeObserver.observe(this._rail);
      } else {
        this._onWindowResize = () => this.checkWidths();
        window.addEventListener('resize', this._onWindowResize, { passive: true });
      }

      this.refresh(true);
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      this._resizeObserver?.disconnect();
      window.removeEventListener('resize', this._onWindowResize);
      this._prev?.removeEventListener('click', this._onPrevious);
      this._next?.removeEventListener('click', this._onNext);
      this._viewport?.removeEventListener('pointerdown', this._onPointerDown);
      this._viewport?.removeEventListener('pointermove', this._onPointerMove);
      this._viewport?.removeEventListener('pointerup', this._onPointerUp);
      this._viewport?.removeEventListener('pointercancel', this._onPointerCancel);
      this._viewport?.removeEventListener('click', this._onClickCapture, true);
      this._viewport?.removeEventListener('keydown', this._onKeyDown);
      this.removeEventListener('focusin', this._onFocusIn);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      this.cancelInteraction();
      this.removeClones();
      this._track?.style.removeProperty('transform');
      this.removeAttribute('data-initialized');
      this.removeAttribute('data-all-fit');
      this.removeAttribute('data-circular-busy');
      this.removeAttribute('data-current-index');
    }

    refresh(initial = false) {
      if (!this._connected || !this._track || !this._viewport) return;
      const focusedCard = this._cards.indexOf(this.querySelector(':focus')?.closest?.('[data-review-card]'));
      const oldIndex = this._currentIndex;
      this.cancelInteraction();
      this._cards = Array.from(this._track.children).filter((card) =>
        card.matches?.('[data-review-card]') && !card.hasAttribute('data-review-clone')
      );
      if (this._cards.length === 0) {
        this.removeClones();
        this._currentIndex = 0;
        this._prev && (this._prev.hidden = true);
        this._next && (this._next.hidden = true);
        this.dataset.initialized = 'true';
        this.dataset.allFit = 'true';
        this.dataset.currentIndex = '0';
        this.updateStatus();
        return;
      }

      this._currentIndex = initial ? 0 : Math.max(0, Math.min(this._cards.length - 1,
        focusedCard >= 0 ? focusedCard : oldIndex));
      this.removeClones();
      this.dataset.initialized = 'true';
      let metrics = this.measure();
      let allFit = metrics.totalWidth <= metrics.fullRailWidth + 1;
      this.dataset.allFit = String(allFit);
      metrics = this.measure();
      const measuredAllFit = metrics.totalWidth <= metrics.fullRailWidth + 1;
      if (measuredAllFit !== allFit) {
        allFit = measuredAllFit;
        this.dataset.allFit = String(allFit);
        metrics = this.measure();
      }
      this._prev && (this._prev.hidden = allFit || this._cards.length < 2);
      this._next && (this._next.hidden = allFit || this._cards.length < 2);

      if (allFit || this._cards.length < 2) {
        this._currentIndex = 0;
        this._track.style.transform = 'translate3d(0, 0, 0)';
      } else {
        this.createClones(metrics);
        this.setPosition(this._currentIndex);
      }
      this.dataset.currentIndex = String(this._currentIndex);
      this.updateStatus();
    }

    measure() {
      const style = getComputedStyle(this._track);
      const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
      const widths = this._cards.map((card) => card.getBoundingClientRect().width);
      const totalWidth = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, widths.length - 1) * gap;
      const railStyle = this._rail ? getComputedStyle(this._rail) : null;
      const railPadding = railStyle ?
        (Number.parseFloat(railStyle.paddingLeft) || 0) + (Number.parseFloat(railStyle.paddingRight) || 0) : 0;
      return {
        gap,
        cardWidth: widths[0] || 0,
        totalWidth,
        viewportWidth: this._viewport.clientWidth,
        fullRailWidth: Math.max(0, (this._rail?.clientWidth || this._viewport.clientWidth) - railPadding)
      };
    }

    checkWidths() {
      if (!this._connected || !this._viewport) return;
      const viewportWidth = this._viewport.clientWidth;
      const railWidth = this._rail?.clientWidth || viewportWidth;
      if (Math.abs(viewportWidth - this._lastViewportWidth) < 1 && Math.abs(railWidth - this._lastRailWidth) < 1) return;
      this._lastViewportWidth = viewportWidth;
      this._lastRailWidth = railWidth;
      this.refresh();
    }

    createClones(metrics) {
      const count = this._cards.length;
      const stride = Math.max(1, metrics.cardWidth + metrics.gap);
      const suffixCount = Math.min(count, Math.max(1, Math.ceil(metrics.viewportWidth / stride)));
      const prefix = this.makeClone(this._cards[count - 1]);
      this._track.insertBefore(prefix, this._cards[0]);
      this._clones.push(prefix);
      for (let i = 0; i < suffixCount; i += 1) {
        const clone = this.makeClone(this._cards[i]);
        this._track.appendChild(clone);
        this._clones.push(clone);
      }
    }

    makeClone(card) {
      const clone = card.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('data-review-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      if (clone.matches(FOCUSABLE)) clone.setAttribute('tabindex', '-1');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll(FOCUSABLE).forEach((node) => node.setAttribute('tabindex', '-1'));
      return clone;
    }

    removeClones() {
      this._track?.querySelectorAll(':scope > [data-review-clone]').forEach((clone) => clone.remove());
      this._clones = [];
    }

    setPosition(index) {
      const card = this._cards[index];
      if (!card) return;
      this._track.style.transform = `translate3d(${-card.offsetLeft}px, 0, 0)`;
    }

    enqueue(direction) {
      if (this._cards.length < 2 || this.dataset.allFit === 'true') return;
      this._queue.push(direction < 0 ? -1 : 1);
      this.runQueue();
    }

    async runQueue() {
      if (this._running || !this._connected) return;
      this._running = true;
      const version = this._interactionVersion;
      this.setBusy(true);
      while (this._queue.length && this._connected) {
        const action = this._queue.shift();
        const count = this._cards.length;
        const nextIndex = typeof action === 'number'
          ? (this._currentIndex + action + count) % count
          : action.index;
        let target;
        let normalizeIndex = null;
        if (typeof action !== 'number') {
          target = this._cards[nextIndex]?.offsetLeft;
        } else if (action > 0 && this._currentIndex === count - 1) {
          const firstSuffix = this._clones[1];
          target = firstSuffix?.offsetLeft ?? this._cards[0].offsetLeft;
          normalizeIndex = 0;
        } else if (action < 0 && this._currentIndex === 0) {
          const prefix = this._clones[0];
          target = prefix?.offsetLeft ?? this._cards[count - 1].offsetLeft;
          normalizeIndex = count - 1;
        } else {
          target = this._cards[nextIndex].offsetLeft;
        }

        await this.animateTo(target ?? this._cards[nextIndex].offsetLeft, this._reducedMotion?.matches ? 0 : DURATION);
        if (version !== this._interactionVersion) break;
        if (!this._connected) break;
        this._currentIndex = nextIndex;
        if (normalizeIndex !== null) this.setPosition(normalizeIndex);
        this.dataset.currentIndex = String(this._currentIndex);
        this.updateStatus();
      }
      if (version === this._interactionVersion) {
        this._running = false;
        if (this._connected) this.setBusy(false);
      }
    }

    animateTo(left, duration) {
      const from = this._track.style.transform || 'translate3d(0, 0, 0)';
      const to = `translate3d(${-left}px, 0, 0)`;
      if (!duration || typeof this._track.animate !== 'function') {
        this._track.style.transform = to;
        return Promise.resolve();
      }
      const animation = this._track.animate([{ transform: from }, { transform: to }], {
        duration, easing: 'ease', fill: 'forwards'
      });
      this._animation = animation;
      return animation.finished.then(() => {
        if (this._animation !== animation) return;
        this._track.style.transform = to;
        animation.cancel();
        this._animation = null;
      }).catch(() => {});
    }

    setBusy(busy) {
      this._busy = busy;
      if (busy) this.dataset.circularBusy = 'true';
      else this.removeAttribute('data-circular-busy');
    }

    cancelInteraction() {
      this._interactionVersion += 1;
      this._queue = [];
      this._animation?.cancel();
      this._animation = null;
      this._running = false;
      this._busy = false;
      this.removeAttribute('data-circular-busy');
    }

    updateStatus() {
      if (!this._status) return;
      const label = this.dataset.circularStatusLabel || 'Review __INDEX__ of __COUNT__';
      this._status.textContent = label
        .replaceAll('__INDEX__', String(this._cards.length ? this._currentIndex + 1 : 0))
        .replaceAll('__COUNT__', String(this._cards.length));
    }

    pointerDown(event) {
      if (event.button !== 0 || event.isPrimary === false) return;
      this._suppressClick = false;
      this._pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false };
    }

    pointerMove(event) {
      const pointer = this._pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        pointer.horizontal = true;
        try { this._viewport.setPointerCapture(pointer.id); } catch (_) {}
      }
    }

    pointerUp(event) {
      const pointer = this._pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const wasHorizontal = pointer.horizontal || (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(event.clientY - pointer.y));
      this._pointer = null;
      if (!wasHorizontal) return;
      this._suppressClick = true;
      this.enqueue(dx < 0 ? 1 : -1);
    }

    keyDown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.enqueue(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.enqueue(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this._cards.length - 1);
      }
    }

    goTo(index) {
      if (this._cards.length < 2 || this.dataset.allFit === 'true') return;
      const targetIndex = Math.max(0, Math.min(this._cards.length - 1, index));
      if (targetIndex === this._currentIndex) return;
      this.cancelInteraction();
      this._currentIndex = targetIndex;
      this.setPosition(targetIndex);
      this.dataset.currentIndex = String(targetIndex);
      this.updateStatus();
    }

    revealFocusedCard(target) {
      const card = target?.closest?.('[data-review-card]');
      if (!card || card.hasAttribute('data-review-clone')) return;
      const index = this._cards.indexOf(card);
      if (index < 0 || this.dataset.allFit === 'true') return;
      this._viewport.scrollLeft = 0;
      const viewportRect = this._viewport.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const fullyVisible = cardRect.left >= viewportRect.left - 1 && cardRect.right <= viewportRect.right + 1;
      if (!fullyVisible && index !== this._currentIndex) this.goTo(index);
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId || event.target?.closest?.('[data-block-id]')?.dataset.blockId;
      const card = (blockId && this._cards.find((item) => item.dataset.blockId === blockId)) ||
        event.target?.closest?.('[data-review-card]');
      if (!card || !this._cards.includes(card)) return;
      this.goTo(this._cards.indexOf(card));
    }
  }

  customElements.define('maxler-circular-reviews', MaxlerCircularReviews);
})();
